import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import VideoCard from "../components/VideoCard";
import LiveBadge from "../components/LiveBadge";
import ShareCampaign from "../components/ShareCampaign";
import GiftsModal from "../components/GiftsModal";
import VerifiedBadge from "../components/VerifiedBadge";
import CreatorSubTiers from "../components/CreatorSubTiers";
import StoryViewer from "../components/StoryViewer";
import { Gift } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

export default function CreatorProfile() {
  const { creatorId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [following, setFollowing] = useState(false);
  const [rank, setRank] = useState(null);
  const [giftsOpen, setGiftsOpen] = useState(false);

  useEffect(() => {
    api.get(`/creators/${creatorId}`).then((r) => setData(r.data));
    api.get(`/awards/creator/${creatorId}/rank`).then((r) => setRank(r.data)).catch(() => {});
  }, [creatorId]);

  const toggleFollow = async () => {
    if (!user) return toast.error("Please sign in");
    const r = await api.post(`/follows/${creatorId}`);
    setFollowing(r.data.following);
    toast.success(r.data.following ? "Following" : "Unfollowed");
  };

  if (!data) return <div className="min-h-screen" />;
  const c = data.creator;

  return (
    <div data-testid="creator-profile" className="pt-32">
      <div className="relative h-64 md:h-80 bg-gradient-to-br from-tv-surface to-black overflow-hidden">
        <img src={c.picture} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30 blur-sm" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent" />
      </div>
      <div className="px-4 sm:px-8 md:px-12 -mt-20 relative">
        <div className="flex items-end gap-6 flex-wrap">
          <img src={c.picture} alt={c.name} className="w-32 h-32 rounded-full border-4 border-[#0A0A0A] object-cover" />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wider text-tv-accent font-bold">{c.role.replace("_", " ")}</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl text-white tracking-tighter flex items-center gap-2">
              {c.name}
              {c.is_top_creator ? <VerifiedBadge gold size={28} /> : c.is_verified && <VerifiedBadge size={26} />}
              {c.verified_ad_partner && <span title="Verified Ad Partner" className="bg-tv-accent text-black text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded">Ad Partner</span>}
            </h1>
            <div className="text-white/60 mt-1 text-sm">{c.follower_count.toLocaleString()} followers · {data.videos.length} videos</div>
          </div>
          <button data-testid="creator-follow-btn" onClick={toggleFollow} className={`px-6 py-3 rounded-full font-bold ${following ? "tv-glass text-white" : "bg-tv-accent text-black hover:bg-yellow-400"}`}>
            {following ? "Following" : "Follow"}
          </button>
          <button
            data-testid="creator-send-gift-btn"
            onClick={() => setGiftsOpen(true)}
            className="px-5 py-3 rounded-full font-bold bg-gradient-to-r from-pink-500 to-purple-500 text-white hover:brightness-110 flex items-center gap-2"
          >
            <Gift className="w-4 h-4" /> Send gift
          </button>
          <ShareCampaign
            creator={c}
            rank={rank?.rank}
            voteUrl={`${window.location.origin}/creator/${creatorId}`}
          />
        </div>
        <GiftsModal open={giftsOpen} onClose={() => setGiftsOpen(false)} creator={c} />
        {rank && (
          <div data-testid="creator-awards-pill" className="mt-4 inline-flex items-center gap-2 tv-glass rounded-full px-4 py-2 border border-tv-accent/40">
            <span className="text-[10px] uppercase tracking-widest text-tv-accent font-bold">{rank.season_name}</span>
            {rank.rank ? (
              <span className="text-white text-sm"><span className="font-display font-black text-tv-accent">#{rank.rank}</span> · {rank.votes} votes</span>
            ) : (
              <span className="text-white/70 text-sm">Not yet ranked · {rank.votes} votes</span>
            )}
          </div>
        )}
        <p className="mt-6 text-white/80 max-w-2xl">{c.bio}</p>
      </div>

      {data.live?.length > 0 && (
        <section className="px-4 sm:px-8 md:px-12 mt-10">
          <h2 className="font-display font-bold text-2xl text-white mb-4 flex items-center gap-3">Live Now <LiveBadge /></h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {data.live.map((l) => <VideoCard key={l.id} item={l} variant="wide" live fullWidth />)}
          </div>
        </section>
      )}

      <CreatorSubTiers creatorId={creatorId} creatorName={c.name} />

      <CreatorHighlights creatorId={creatorId} />

      <CreatorTabs creatorId={creatorId} videos={data.videos} />
    </div>
  );
}

function CreatorHighlights({ creatorId }) {
  const [highlights, setHighlights] = useState([]);
  const [viewer, setViewer] = useState(null); // { bubbles, startIndex }
  useEffect(() => {
    api.get(`/creators/${creatorId}/highlights`).then((r) => setHighlights(r.data || [])).catch(() => {});
  }, [creatorId]);

  if (highlights.length === 0) return null;
  const openHighlight = (h) => {
    const stories = (h.stories || []).map((s) => ({
      id: s.id, title: s.title, video_url: s.video_url,
      thumbnail_url: s.thumbnail_url, duration_sec: s.duration_sec || 8,
      created_at: s.created_at, creator_id: s.creator_id,
      creator_name: s.creator_name, creator_avatar: s.creator_avatar,
    }));
    if (stories.length === 0) return;
    const bubble = {
      kind: "highlight",
      owner_id: creatorId,
      owner_name: stories[0].creator_name || "Creator",
      owner_avatar: stories[0].creator_avatar || "",
      is_live: false,
      stories,
    };
    setViewer({ bubbles: [bubble], startIndex: 0 });
  };

  return (
    <>
      <section data-testid="creator-highlights" className="px-4 sm:px-8 md:px-12 mt-8">
        <h2 className="font-display font-black text-xl text-white mb-3">Highlights</h2>
        <div className="flex gap-4 overflow-x-auto pb-2">
          {highlights.map((h) => (
            <button
              key={h.id}
              data-testid={`creator-highlight-${h.id}`}
              onClick={() => openHighlight(h)}
              className="shrink-0 flex flex-col items-center gap-2 w-20 focus:outline-none group"
            >
              <div className="w-[72px] h-[72px] rounded-full p-[3px] bg-gradient-to-tr from-fuchsia-500 via-orange-400 to-yellow-400 group-hover:scale-105 transition">
                <div className="w-full h-full rounded-full bg-[#0A0A0A] p-[2px]">
                  <div className="w-full h-full rounded-full overflow-hidden bg-white/5">
                    {h.cover_url ? (
                      <img src={h.cover_url} alt="" className="w-full h-full object-cover" />
                    ) : h.stories?.[0]?.thumbnail_url ? (
                      <img src={h.stories[0].thumbnail_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/40 text-2xl">★</div>
                    )}
                  </div>
                </div>
              </div>
              <span className="text-[11px] text-white/80 font-semibold truncate max-w-full leading-tight">{h.title}</span>
            </button>
          ))}
        </div>
      </section>
      {viewer && (
        <StoryViewer
          bubbles={viewer.bubbles}
          startIndex={viewer.startIndex}
          onClose={() => setViewer(null)}
          onStorySeen={() => {}}
        />
      )}
    </>
  );
}

function CreatorTabs({ creatorId, videos }) {
  const [tab, setTab] = useState("videos");
  const [products, setProducts] = useState([]);
  const { add } = useCart();

  useEffect(() => {
    api.get(`/shop/products?creator_id=${creatorId}&limit=60`).then((r) => setProducts(r.data || [])).catch(() => {});
  }, [creatorId]);

  return (
    <section className="px-4 sm:px-8 md:px-12 my-12">
      <div className="flex gap-2 mb-4 border-b border-white/10 pb-3">
        <button data-testid="creator-tab-videos" onClick={() => setTab("videos")} className={`px-4 py-2 rounded-full text-sm font-bold ${tab === "videos" ? "bg-tv-accent text-black" : "text-white/70 hover:text-white"}`}>Videos ({videos.length})</button>
        <button data-testid="creator-tab-shop" onClick={() => setTab("shop")} className={`px-4 py-2 rounded-full text-sm font-bold ${tab === "shop" ? "bg-tv-accent text-black" : "text-white/70 hover:text-white"}`}>Shop ({products.length})</button>
      </div>
      {tab === "videos" && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {videos.map((v) => <VideoCard key={v.id} item={v} variant="wide" fullWidth />)}
          {!videos.length && <div className="col-span-full text-white/60">No videos yet.</div>}
        </div>
      )}
      {tab === "shop" && (
        products.length === 0 ? (
          <div data-testid="creator-shop-empty" className="text-white/60">No products yet.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="creator-shop-grid">
            {products.map((p) => (
              <div key={p.id} data-testid={`creator-product-${p.id}`} className="tv-glass rounded-xl overflow-hidden group border border-white/5 hover:border-tv-accent/40 transition">
                <Link to={`/shop/product/${p.id}`} className="block aspect-square bg-black">
                  <img src={(p.images || [])[0] || "https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=400&q=80"} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </Link>
                <div className="p-3">
                  <div className="text-white font-semibold text-sm line-clamp-2">{p.title}</div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="text-tv-accent font-black">${p.price_usd.toFixed(2)}</div>
                    <button data-testid={`creator-product-add-${p.id}`} onClick={() => add(p.id, 1)} className="bg-tv-accent hover:bg-yellow-400 text-black font-bold text-xs px-3 py-1.5 rounded-full">
                      Add
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </section>
  );
}
